import { useMutation } from "@tanstack/react-query"
import axios from "axios"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { DictType } from "@pma/types"
import { SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { Edit2, FolderPlus } from "lucide-react"
import { Button } from "@components/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import React, { useEffect, useState } from "react"
import { Input } from "@components/common/input"
import { Separator } from "@components/common/separator"
import { toast } from "@hooks/use-toast"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import useTranslator from "@hooks/use-translator"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@pma/components/common/form"
import { cn } from "@pma/utils/helpers"

interface Props {
  parent: DictType
  revalidate: CallableFunction
  isMenuView?: boolean
  type: Number
  setActiveFolder?: React.Dispatch<React.SetStateAction<DictType>>
  setBreadcrumbs?: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  folderItems?: { [id: number]: any[] }
  setFolderItems?: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  refetchAllFolder?: () => void
}

type FolderOrFile = {
  id: number
  name: string
  as: "folder" | "file"
  [key: string]: any
}

type FolderStructure = {
  [folderId: number]: FolderOrFile[]
}

type Folder = {
  id: number
  name: string
  [key: string]: any
}

type FolderStructureForBreadcrumbs = {
  [key: string]: Folder[]
}

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .nonempty({ message: "Folder name is required" })
    .min(2, { message: "Folder name must be at least 2 characters long" })
    .max(50, { message: "Folder name can only be up to 50 characters long" }),
  parent_id: z.number().optional(),
  folder_id: z.number().optional(),
})

const AddSubFolder = ({
  parent,
  revalidate,
  isMenuView = false,
  type,
  setActiveFolder,
  setBreadcrumbs,
  folderItems,
  setFolderItems,
  refetchAllFolder = () => { },
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const { translate } = useTranslator()

  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  })

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = form

  useEffect(() => {
    let object: Record<string, any> = {}
    if (type == 1) {
      object["name"] = parent?.name
      object["folder_id"] = parent?.id
    } else {
      object["name"] = "Subfolder"
      object["parent_id"] = parent?.id
    }
    reset(object)
  }, [parent])

  const addSubFolder = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + "/folders/add",
      { ...payload },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response?.data
  }

  const addSubFolderMutation = useMutation({
    mutationKey: ["add-sub-folder", parent?.id],
    mutationFn: addSubFolder,
    onSuccess: (data) => {
      // Revalidate files/folders when folder is added
      revalidate()
      refetchAllFolder()
      toast({
        title: translate("Success"),
        description: translate("Subfolder added"),
        variant: "success",
      })
      setIsOpen(false)
      reset()
      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
    },
    onError: (error: DictType) => {
      const message = error?.response.data.message
      toast({
        title: translate("Error"),
        description: message || translate("Error adding subfolder"),
        variant: "error",
      })
    },
  })

  const renameSubFolder = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + "/folders/rename",
      { ...payload },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response?.data
  }

  function renameFolderById(
    structure: FolderStructureForBreadcrumbs,
    targetId: number,
    newName: string,
  ): FolderStructureForBreadcrumbs {
    const newStructure: FolderStructureForBreadcrumbs = {}

    for (const key in structure) {
      newStructure[key] = structure[key].map((folder) => {
        if (folder.id === targetId) {
          return {
            ...folder,
            name: newName,
          }
        }
        return { ...folder } // shallow copy to ensure immutability
      })
    }

    return newStructure
  }

  const renameSubFolderMutation = useMutation({
    mutationKey: ["rename-sub-folder"],
    mutationFn: renameSubFolder,
    onSuccess: (data) => {
      // Revalidate files/folders when folder is added
      const updateFolderItems = renameFolder(folderItems ?? {}, parent?.name, data?.name)
      if (setFolderItems) {
        setFolderItems(updateFolderItems)
      }
      if (setActiveFolder) {
        setActiveFolder((pre) => {
          if (pre.id == parent?.id) {
            return { ...pre, name: data?.name }
          }
          return pre
        })
      }

      if (setBreadcrumbs) {
        setBreadcrumbs((pre) => {
          const result = renameFolderById(pre, parent.id, data?.name)
          return result
        })
      }
      toast({
        title: translate("Success"),
        description: translate("Subfolder renamed"),
        variant: "success",
      })
      setIsOpen(false)
      reset()
      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
    },
    onError: (error: DictType) => {
      const message = error?.response.data.message
      toast({
        title: translate("Error"),
        description: message || translate("Error rename subfolder"),
        variant: "error",
      })
    },
  })

  function renameFolder(
    structure: FolderStructure,
    oldName: string,
    newName: string,
  ): FolderStructure {
    const updatedStructure: FolderStructure = {}

    const renameRecursive = (items: FolderOrFile[]): FolderOrFile[] => {
      return items.map((item) => {
        // If it's a folder and the name matches
        if (item.as === "folder" && item.name === oldName) {
          item.name = newName
        }

        // Check if this folder has children in the structure
        if (item.as === "folder" && structure[item.id]) {
          updatedStructure[item.id] = renameRecursive(structure[item.id])
        }

        return item
      })
    }

    // Start from the root (you can change this if needed)
    for (const folderId in structure) {
      if (!updatedStructure[folderId]) {
        updatedStructure[Number(folderId)] = renameRecursive(structure[Number(folderId)])
      }
    }

    return updatedStructure
  }

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (type == 1) renameSubFolderMutation.mutate(data)
    else addSubFolderMutation.mutate(data)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger>
          <PopoverTrigger asChild>
            {isMenuView ? (
              <div className='flex items-center gap-2'>
                <Button size={"icon"} className='h-6 w-7' variant={"ghost"}>
                  {type === 1 ? (
                    <Edit2 className='text-yellow-500' size={16} />
                  ) : (
                    <FolderPlus className='text-primary' size={16} />
                  )}
                </Button>
                <span className='text-sm'>
                  {`${translate(type === 1 ? "Rename folder" : "New folder")}`}
                </span>
              </div>
            ) : (
              <Button variant={"ghost"} size={"icon"} className='bg-slate-200/50'>
                {type === 1 ? (
                  <Edit2 className='text-yellow-500' size={16} />
                ) : (
                  <FolderPlus className='text-primary' size={16} />
                )}
              </Button>
            )}
          </PopoverTrigger>
        </TooltipTrigger>
        {!isMenuView && (
          <TooltipContent>
            {translate(type === 1 ? "Rename folder" : "Add a new folder")}
          </TooltipContent>
        )}
      </Tooltip>
      <PopoverContent className=''>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='space-y-2'>
              <h4 className='text-sm font-semibold'>
                {type === 1
                  ? `${translate("Rename Subfolder")}`
                  : `${translate("Add Subfolder")} ${translate("to")} ${parent?.name}`}
              </h4>
              <Separator />
              <div className='w-full'>
                <div className='flex w-full flex-col gap-4'>
                  <FormField
                    control={control}
                    name='name'
                    render={({ field }) => (
                      <FormItem className='mb-4 flex flex-col items-start justify-start space-y-2'>
                        <FormControl>
                          <Input
                            placeholder={translate("Subfolder")}
                            className={cn(
                              `w-full ${errors?.name ? "border-red-500 focus-visible:ring-red-500" : ""}`,
                            )}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
            <Button
              className='my-2 w-full'
              size={"sm"}
              loading={
                type == 1 ? renameSubFolderMutation?.isPending : addSubFolderMutation?.isPending
              }
              disabled={
                type == 1 ? renameSubFolderMutation?.isPending : addSubFolderMutation?.isPending
              }
            >
              {translate(type == 1 ? "Change" : "Add")}
            </Button>
          </form>
        </Form>
      </PopoverContent>
    </Popover>
  )
}

export default AddSubFolder
